import {
  AcquiringChannel,
  ApplicationAction,
  ApplicationActionType,
  Catalogue,
  CatalogueItem,
} from 'src/features/common';
import { CardMachine } from 'src/features/common/types/application/card-machine';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { serialNumberPattern } from 'src/helpers/formatting';

export interface FormattedMachine {
  productId: string;
  acquiringChannel: AcquiringChannel;
  serialNumbers: string[];
  friendlyTerminalNames: string[];
}

export enum SERIAL_NUMBER {
  MAX = 'N5',
  KWIKA = 'K3R',
  MAX_2 = 'N86',
}

export function extractSerialNumber(text: string) {
  const match = text.match(serialNumberPattern);

  return match ? match[0] : null;
}

export function isSerialNumberValid(text: string) {
  return serialNumberPattern.test(text);
}

export function getProduct(
  products: CatalogueItem[] | undefined,
  serialNumber: string,
): CatalogueItem {
  const productSearchMap = new Map([
    [SERIAL_NUMBER.MAX_2, DEVICE_NAMES.MAX_2],
    [SERIAL_NUMBER.KWIKA, DEVICE_NAMES.VODAPAY_KWIKA],
    [SERIAL_NUMBER.MAX, DEVICE_NAMES.VODAPAY_MAX],
  ]);

  const key = serialNumber.startsWith(SERIAL_NUMBER.MAX)
    ? SERIAL_NUMBER.MAX
    : <SERIAL_NUMBER>serialNumber.substring(0, 3);

  const searchValue = productSearchMap.get(key) || '';

  const product = (products || []).find((item) =>
    item.device.title.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
  );

  return product || ({} as CatalogueItem);
}

export function groupAndFormatSerialNumbers(array: CardMachine[]): FormattedMachine[] {
  const grouped: { [key: string]: FormattedMachine } = {};
  for (const item of array) {
    const { productId, acquiringChannel, barcode, name = '' } = item;
    const code = barcode.trim().replace(/\s/g, '');
    if (grouped[productId]) {
      grouped[productId].serialNumbers.push(code);
      grouped[productId].friendlyTerminalNames.push(name);
    } else {
      grouped[productId] = {
        productId,
        acquiringChannel,
        serialNumbers: [code],
        friendlyTerminalNames: [name],
      };
    }
  }

  return Object.values(grouped);
}

export function createDeviceActivationActions(
  formattedCardMachines: FormattedMachine[],
  catalogue: Catalogue,
  isDpo?: boolean,
): ApplicationAction[] {
  const actions: ApplicationAction[] = [];

  for (const formattedMachine of formattedCardMachines) {
    actions.push({
      type: ApplicationActionType.ATTACH_SERIAL_NUMBER,
      productId: formattedMachine.productId,
      serialNumber: formattedMachine.serialNumbers,
      friendlyTerminalName: formattedMachine.friendlyTerminalNames,
    });
    if (isDpo) {
      actions.push({
        type: ApplicationActionType.UPDATE_CART_QUANTITY,
        productId: formattedMachine.productId,
        acquiringChannel: formattedMachine.acquiringChannel,
        quantity: formattedMachine.serialNumbers.length,
        catalogue,
      });
    } else {
      actions.push({
        type: ApplicationActionType.UPDATE_BASKET_QUANTITY,
        productId: formattedMachine.productId,
        acquiringChannel: formattedMachine.acquiringChannel,
        quantity: formattedMachine.serialNumbers.length,
        catalogue,
      });
    }
  }

  return actions;
}

export function create3PODeviceActivationActions(
  formattedCardMachines: FormattedMachine[],
): ApplicationAction[] {
  const actions: ApplicationAction[] = [];

  for (const formattedMachine of formattedCardMachines) {
    actions.push({
      type: ApplicationActionType.ATTACH_SERIAL_NUMBER,
      productId: formattedMachine.productId,
      serialNumber: formattedMachine.serialNumbers,
      friendlyTerminalName: formattedMachine.friendlyTerminalNames,
    });
  }

  return actions;
}
